import { useState, useCallback, useEffect, useRef, useMemo, useContext, createContext, createElement } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useSessionContext } from '../contexts/SessionContext';
import { apiFetch } from '../lib/api';
import { useI18n } from '../i18n';
import type { PersonaEmotion, AiChange, AiFocus, AiPlanStepWire, AiNavigationTarget, AiNavigationReceiptStatus, AiActionNote, AiActionStatus } from '@roll1/shared';
import { useUserUiPrefs } from './useUserUiPrefs';
import { onAiAsk, onApplyHint, onRemarkFix, onRemarksFound } from '../lib/aiBridge';
import type { TiaStageState } from '../components/tia/TiaStage';
import { itemsWithConfirmed, itemsWithOutward, itemsWithPlan, itemsWithPlanStale, itemsWithPlanDismissed, itemsWithTurn, itemsWithActionStatus, itemsWithPlanStatus, type ActionStatusFrame } from './aiTurnItems';
import { useHiddenAiResults } from './useHiddenAiResults';
import { PERSONA_EMOTIONS } from '@roll1/shared';
import { toast } from 'sonner';

// --- Types ---

/** Auto: the AI does as asked and shows what changed; suggest: it changes nothing (its write calls become a plan). */
export type AiChatMode = 'auto' | 'suggest';
export type AiChatMessageRole = 'user' | 'assistant' | 'error' | 'tool' | 'thinking' | 'notice' | 'changes' | 'plan' | 'outward' | 'options' | 'topic' | 'topic-end';

/** The card of one answer: what it changed, as the server's journal has it (ai:turn-changes). */
export interface AiTurnData {
  turnId: string;
  changes: AiChange[];
}

/** A plan the AI offered while changes are not allowed (ai:plan): the card with «Do it». */
export interface AiPlanData {
  planId: string;
  steps: AiPlanStepWire[];
  state: 'open' | 'applying' | 'dismissing' | 'dismissed' | 'applied' | 'stale' | 'unknown';
  staleReason?: 'beat_changed' | 'objective_changed' | 'missing' | 'gone';
  /** P5: a step failed after it started — how much of what was applied went back. */
  rolledBack?: 'all' | 'partial' | 'none';
}

/** Something the AI wanted to show to the players / delete: the button in the answer asks the GM (ai:needs-confirmation). */
export interface AiOutwardData {
  actionId: string;
  kind: 'show' | 'delete';
  tool: string;
  target: { kind: 'journal' | 'page' | 'location'; id: string; name: string };
  done: boolean;
  /** The server no longer has the action (a restart, a refusal): the button is out of date. */
  stale?: boolean;
  /** P8: the AI had read material someone else may have written before it offered this (not proof of what led to it). */
  fromUntrustedSource?: true | 'unknown';
  /** P5: what the server's record says became of it (ai:action-status); absent — pending as offered. */
  status?: AiActionStatus;
  note?: AiActionNote;
  replacedBy?: string;
}

/** The options Tia offered for a remark's topic (ai:options). */
export interface AiOptionsData {
  remarkKey: string;
  options: Array<{ id: string; label: string; kind: 'fix' | 'dismiss' | 'ask'; prompt?: string }>;
}

/** The topic of a conversation: a remark being discussed. */
export interface AiTopicData {
  remarkKey: string;
  title: string;
  /** open: being discussed; ended: closed by a choice or by the button (its line is the next message). */
  open: boolean;
}

/** An undo that met the GM's own later edit: asking what to do (ai:undo-conflict). */
export interface AiUndoConflict {
  turnId: string;
  /** What the undo was for: the changes (absent: the whole answer). */
  changeIds: string[] | undefined;
  conflicts: string[];
}

export interface AiToolExecution {
  toolName: string;
  args: Record<string, unknown>;
  status: 'running' | 'done' | 'error';
  result?: string;
  success?: boolean;
}

export interface AiChatMessage {
  suggestedReplies?: string[];
  id: string;
  role: AiChatMessageRole;
  content: string;
  toolData?: AiToolExecution;
  turnData?: AiTurnData;
  planData?: AiPlanData;
  outwardData?: AiOutwardData;
  optionsData?: AiOptionsData;
  topicData?: AiTopicData;
  timestamp: string;
  emotion?: PersonaEmotion;
  /** A request: where the GM was when it was sent (the caption over the bubble). */
  focus?: AiFocus;
}

/** Strip [EMOTION]: ... line from streaming content (client-side complement to server parsing) */
const EMOTION_LINE_REGEX = /\n?\[EMOTION\]:\s*\w+\s*$/;

function stripEmotionLine(content: string): string {
  return content.replace(EMOTION_LINE_REGEX, '').trimEnd();
}

export interface AiConversationSummary {
  id: string;
  title: string | null;
  status: string;
  messageCount: number;
  totalTokensUsed: number;
  createdAt: string;
  updatedAt: string;
}

/** Base interface shared by GM and Player AI chat hooks */
export interface AiChatBase {
  messages: AiChatMessage[];
  isStreaming: boolean;
  error: string | null;
  conversationId: string | null;
  sendMessage: (text: string) => Promise<void>;
  resetChat: () => void;
  abortGeneration: () => void;
  isAborted: boolean;
  input: string;
  setInput: (text: string) => void;
  conversations: AiConversationSummary[];
  loadConversations: (append?: boolean) => Promise<void>;
  loadConversation: (conversationId: string) => Promise<void>;
  isLoadingHistory: boolean;
  conversationTitle?: string | null;
  conversationsTotal?: number;
  renameConversation?: (id: string, title: string) => Promise<void>;
  /** Last received emotion from ai:done (for header display) */
  lastEmotion: PersonaEmotion | undefined;
  /** The personal box «Allow changes» (User.uiPreferences.aiAllowEdits): the request asks 'auto' when ticked, 'suggest' otherwise. */
  allowEdits?: boolean;
  setAllowEdits?: (on: boolean) => void;
  /** The state of THIS chat's request, for Tia's stage. */
  stageState?: TiaStageState;
  /** How many remarks the last model pass found (the stage's «found N remarks»). */
  notesCount?: number;
  /** GJ-34: the server has no model key (503 ai_unavailable) — Tia says so, instead of promising a retry will help. */
  aiUnavailable?: boolean;
  /** Where the GM is: the very object that goes with a request (the line «Tia sees» is built from it). */
  getFocus?: () => AiFocus | undefined;
  /** «Do it» of a plan card. */
  applyPlan?: (planId: string, stepIds: string[]) => void;
  dismissPlan?: (planId: string) => void;
  hiddenResultIds?: string[];
  setResultHidden?: (id: string, hidden: boolean) => void;
  /** The button outward after its confirmation. */
  runConfirmed?: (actionId: string) => void;
  /** A choice among the options of a remark's topic. */
  pickOption?: (optionsMessageId: string, optionId: string) => void;
  /** «close the topic» without a choice. */
  closeTopic?: (topicMessageId: string) => void;
  /** Take back an answer by the server's change journal (the GM's and the player's own). */
  undoTurn?: (turnId: string, changeIds?: string[]) => void;
}

export interface UseAiChatReturn extends AiChatBase {
  allowEdits: boolean;
  setAllowEdits: (on: boolean) => void;
  stageState: TiaStageState;
  notesCount: number;
  getFocus: () => AiFocus | undefined;
  applyPlan: (planId: string, stepIds: string[]) => void;
  runConfirmed: (actionId: string) => void;
  pickOption: (optionsMessageId: string, optionId: string) => void;
  closeTopic: (topicMessageId: string) => void;
  /** Текстовая модель, выбранная в чате; undefined — умолчание кампании (пакет 3 переезда AI). */
  textModel: string | undefined;
  setTextModel: (id: string | undefined) => void;
  /** Модель иллюстраций, выбранная в чате; undefined — умолчание кампании. */
  imageModel: string | undefined;
  setImageModel: (id: string | undefined) => void;
  undoTurn: (turnId: string, changeIds?: string[]) => void;
  /** The undo that met the GM's own edit and waits for the answer. */
  undoConflict: AiUndoConflict | null;
  resolveUndoConflict: (choice: 'restore' | 'keep-mine' | 'cancel') => void;
  thinkingContent: string;
}

// --- Constants ---

export const STREAMING_TIMEOUT_MS = 300_000;
// Generous cap so pasting a full character sheet / long prompt goes through
// (was 10k — too small for that). Still bounded to protect LLM context/cost.
export const MAX_MESSAGE_LENGTH = 50_000;

// --- Hook ---

/** What the Studio actually did with Tia's target (P6) — sent back as the navigation receipt. */
export interface AiNavigationReport { status: AiNavigationReceiptStatus; uiRevision?: number }

export interface UseAiChatOptions {
  /**
   * Opens Tia's target; `foreground` — this window is in front and focused. Returns what happened (at once, or once
   * the screen shows it): the receipt is built from it, not from the frame's arrival. Without it — or when it never
   * settles — no receipt goes and the server reports no-ack.
   */
  onOpenEntity?: (target: AiNavigationTarget, how: { foreground: boolean }) => AiNavigationReport | Promise<AiNavigationReport>;
  /** AT-4: where the GM is, read when the message is sent. */
  getFocus?: () => AiFocus | undefined;
  /** A player's chat: /ai/player-chat, no focus, no models, no GM extras. */
  player?: boolean;
  /**
   * «Замечания» → this chat (aiBridge): present only for the GM chat that is on screen; open() opens the column /
   * window if it is closed.
   */
  bridge?: { open: () => void };
}

function useAiChatCore(options: UseAiChatOptions = {}): UseAiChatReturn {
  const { t } = useI18n();
  const getFocusRef = useRef(options.getFocus);
  getFocusRef.current = options.getFocus;
  const openEntityRef = useRef(options.onOpenEntity);
  openEntityRef.current = options.onOpenEntity;
  const [undoConflict, setUndoConflict] = useState<AiUndoConflict | null>(null);
  // The undo asked for and not answered yet: its toast comes with the answer.
  const pendingUndoRef = useRef<{ turnId: string; all: boolean } | null>(null);
  const lastUndoIdsRef = useRef<string[] | undefined>(undefined);
  // When the GM sent a new message over a running answer: its «aborted» frame is the answer's end, not an event.
  const awaitingInterruptRef = useRef(false);
  const [messages, setMessages] = useState<AiChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const isPlayer = options.player === true;
  // The personal box «Allow changes»: ticked → the request asks 'auto', unticked → 'suggest' (a plan). One setting of the user,
  // the same in the Studio column and at the table; a player has his own.
  const { prefs: uiPrefs, setPref: setUiPref, flush: flushUiPrefs } = useUserUiPrefs();
  const allowEdits = uiPrefs.aiAllowEdits === true;
  // Written at once (not after the debounce): @Tia reads the box on the server.
  const setAllowEdits = useCallback((on: boolean): void => { setUiPref('aiAllowEdits', on); flushUiPrefs(); }, [setUiPref, flushUiPrefs]);
  const mode: AiChatMode = allowEdits ? 'auto' : 'suggest';
  const modeRef = useRef<AiChatMode>(mode);
  modeRef.current = mode;
  // The state of THIS chat's request (Tia's stage follows it).
  const [stageState, setStageState] = useState<TiaStageState>('idle');
  const [notesCount, setNotesCount] = useState(0);
  const [aiUnavailable, setAiUnavailable] = useState(false);
  // «Discuss»: the remark that is the topic of the conversation now; the one to close when its answer is done.
  const topicRef = useRef<{ remarkKey: string } | null>(null);
  const pendingCloseRef = useRef<string | null>(null);
  /** The fix answer that should close the remark changed something (its card came) — only then the remark is closed. */
  const fixAppliedRef = useRef(false);
  const awaitingFixRef = useRef(false);
  // A hint marked from the table: its toast comes with the server's card, not with the click.
  const hintToastRef = useRef(false);
  // What this instance sent and waits an answer for (a refusal is shown by the one that asked, once).
  const applyingPlanRef = useRef<string | null>(null);
  const runningActionRef = useRef<string | null>(null);
  const undoSentAtRef = useRef(0);
  // Every conversation id this chat has been in (a fix may name its own first): a card of any of them is this chat's.
  const knownConvIdsRef = useRef<Set<string>>(new Set());
  const [textModel, setTextModel] = useState<string | undefined>(undefined);
  const [imageModel, setImageModel] = useState<string | undefined>(undefined);
  // Через ссылки: sendMessage не пересоздаётся при смене модели и не держит старое значение.
  const textModelRef = useRef<string | undefined>(undefined);
  const imageModelRef = useRef<string | undefined>(undefined);
  textModelRef.current = textModel;
  imageModelRef.current = imageModel;
  const [thinkingContent, setThinkingContent] = useState('');
  const [isAborted, setIsAborted] = useState(false);
  const [input, setInput] = useState('');
  const [conversations, setConversations] = useState<AiConversationSummary[]>([]);
  const [conversationTitle, setConversationTitle] = useState<string | null>(null);
  const [conversationsTotal, setConversationsTotal] = useState(0);
  const conversationDrafts = useRef(new Map<string, string>());
  const currentInput = useRef(input);
  currentInput.current = input;
  const historyRequest = useRef(0);
  const loadingConversation = useRef(false);
  const manualTitles = useRef(new Map<string, string>());
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const { mp, auth } = useSessionContext();
  const sessionId = mp.session?.id;
  const { hiddenResultIds, setResultHidden } = useHiddenAiResults(auth?.user?.id, sessionId);

  // Refs for use in WS handler closures
  const conversationIdRef = useRef<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isStreamingRef = useRef(false);
  /** The answer now coming (its id from POST /ai/chat) and the ones this window left: their late frames are dropped. */
  const runIdRef = useRef<string | null>(null);
  const staleRunIdsRef = useRef<Set<string>>(new Set());
  /** «Stop» pressed while the server was still taking the message (its id not known yet): stop that answer once it is. */
  const stopPendingRef = useRef(false);
  const leaveRun = (): void => { if (runIdRef.current !== null) staleRunIdsRef.current.add(runIdRef.current); runIdRef.current = null; };
  const thinkingContentRef = useRef('');
  const pendingDeltaRef = useRef('');
  const rafIdRef = useRef<number>(0);

  // Sync refs with state
  useEffect(() => {
    conversationIdRef.current = conversationId;
    if (conversationId !== null) knownConvIdsRef.current.add(conversationId);
  }, [conversationId]);

  useEffect(() => {
    isStreamingRef.current = isStreaming;
  }, [isStreaming]);

  // --- Streaming timeout ---

  function clearStreamingTimeout() {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }

  function resetStreamingTimeout() {
    clearStreamingTimeout();
    timeoutRef.current = setTimeout(() => {
      dropTopicState();
      setIsStreaming(false);
      setStageState('error');
      // Abort server-side stream to stop wasting resources/tokens
      if (conversationIdRef.current) {
        mp.send({ type: 'ai:abort', conversationId: conversationIdRef.current });
      }
      setMessages(prev => [...prev, {
        id: crypto.randomUUID(),
        role: 'error' as const,
        content: t('aiError.timeout'),
        timestamp: new Date().toISOString(),
      }]);
    }, STREAMING_TIMEOUT_MS);
  }

  // Cleanup timeout and RAF on unmount
  useEffect(() => {
    return () => {
      clearStreamingTimeout();
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, []);

  // --- WS event handlers ---

  function flushTextDelta(delta: string) {
    setMessages(prev => {
      const last = prev[prev.length - 1];
      if (last && last.role === 'assistant') {
        return [
          ...prev.slice(0, -1),
          { ...last, content: last.content + delta },
        ];
      }
      return [...prev, {
        id: crypto.randomUUID(),
        role: 'assistant' as const,
        content: delta,
        timestamp: new Date().toISOString(),
      }];
    });
  }

  function scheduleFlush() {
    if (rafIdRef.current) return;
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = 0;
      const delta = pendingDeltaRef.current;
      if (!delta) return;
      pendingDeltaRef.current = '';
      flushTextDelta(delta);
    });
  }

  /** The text streamed so far goes into the list before a card / button that belongs under it. */
  function flushPending() {
    if (pendingDeltaRef.current) {
      if (rafIdRef.current) { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = 0; }
      flushTextDelta(pendingDeltaRef.current);
      pendingDeltaRef.current = '';
    }
  }

  /** An answer that did not succeed (error, refusal, stop): the topic and the remark to close with it do not carry over to the next one. */
  function dropTopicState() {
    topicRef.current = null;
    pendingCloseRef.current = null;
  }

  /** The topic of a remark is over (a choice, «close the topic», the answer done): its options go, its line says how. */
  function endTopic(remarkKey: string, text: string) {
    topicRef.current = null;
    setMessages(prev => [
      ...prev
        .filter(m => !(m.role === 'options' && m.optionsData?.remarkKey === remarkKey))
        .map(m => (m.role === 'topic' && m.topicData?.remarkKey === remarkKey && m.topicData.open ? { ...m, topicData: { ...m.topicData, open: false } } : m)),
      { id: crypto.randomUUID(), role: 'topic-end' as const, content: text, timestamp: new Date().toISOString() },
    ]);
  }

  function handleAiError(message: string, timestamp: string) {
    dropTopicState();
    setIsStreaming(false);
    clearStreamingTimeout();
    setMessages(prev => [...prev, {
      id: crypto.randomUUID(),
      role: 'error' as const,
      content: message,
      timestamp,
    }]);
  }

  // --- WS subscription ---

  useEffect(() => {
    const unsub = mp.onMessage((msg) => {
      if (!msg.type.startsWith('ai:') && msg.type !== 'command:error') return;
      // A card of @Tia belongs to her reply in the table chat (useMentionCards), not to this conversation.
      if ((msg as { chatMessageId?: string }).chatMessageId !== undefined) return;

      // Filter by conversationId if known: a frame of any conversation this chat has been in is this chat's.
      if ('conversationId' in msg) {
        const frameConv = (msg as { conversationId: string }).conversationId;
        if (conversationIdRef.current) {
          if (!knownConvIdsRef.current.has(frameConv)) return;
        } else if (!isStreamingRef.current) {
          // No conversation yet and no answer coming: a frame of someone else's answer is not this chat's.
          return;
        }
      }

      // The end of an answer this window already left (stopped, or replaced by a newer message) is not the end of the one
      // now coming (owner 31.10: «Стоп», a new message, then a second «stopped by the GM» and a silently running answer).
      if ((msg.type === 'ai:done' || msg.type === 'ai:aborted' || msg.type === 'ai:error')) {
        const runId = (msg as { runId?: string }).runId;
        if (runId !== undefined && staleRunIdsRef.current.has(runId)) return;
      }

      // The «no answer» timer runs only while an answer is coming — a card or a question after an undo is not one.
      if (isStreamingRef.current && msg.type !== 'ai:turn-changes' && msg.type !== 'ai:undo-conflict' && msg.type !== 'command:error') resetStreamingTimeout();

      switch (msg.type) {
        case 'ai:open-entity': {
          // Navigation is live, local to the initiating chat; restoring history never navigates.
          const open = openEntityRef.current;
          if (!isStreamingRef.current || open === undefined) break;
          const foreground = document.visibilityState === 'visible' && document.hasFocus();
          let report: AiNavigationReport | Promise<AiNavigationReport>;
          try { report = open(msg.target, { foreground }); } catch { report = { status: 'error' }; }
          // An old server's frame has no requestId and waits for nothing.
          const requestId = msg.requestId;
          if (requestId === undefined) break;
          const { conversationId: frameConversationId, target } = msg;
          const answer = (done: AiNavigationReport): void => {
            mp.send({
              type: 'ai:navigation-receipt', requestId, conversationId: frameConversationId,
              target: { kind: target.kind, id: target.id }, status: done.status,
              ...(done.uiRevision !== undefined ? { uiRevision: done.uiRevision } : {}),
            });
          };
          void Promise.resolve(report).then(answer, () => answer({ status: 'error' }));
          break;
        }
        case 'ai:thinking-delta':
          // The model's reasoning is never shown (owner, 23.10): an old server's frame is dropped here.
          break;
        case 'ai:text-delta':
          pendingDeltaRef.current += (msg as { delta: string }).delta;
          scheduleFlush();
          break;
        case 'ai:aborted': {
          // The server closes the interrupted answer BEFORE it answers the new request, so its «aborted» comes first:
          // exactly that one is the old answer's end, not a «Stop» of the new one.
          const reason = (msg as { reason?: 'stopped' | 'superseded' | 'timeout' }).reason;
          if (awaitingInterruptRef.current) {
            awaitingInterruptRef.current = false;
            break;
          }
          // An older answer ended by this window's new message — one this window did not know was running (the chat was
          // reopened, the Studio re-entered): the new answer goes on; it is not a «Stop» (owner 31.10).
          if (reason === 'superseded') break;
          if (pendingDeltaRef.current) {
            if (rafIdRef.current) { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = 0; }
            flushTextDelta(pendingDeltaRef.current);
            pendingDeltaRef.current = '';
          }
          const wasStreaming = isStreamingRef.current;
          dropTopicState();
          setIsStreaming(false);
          setIsAborted(true);
          setStageState('idle');
          clearStreamingTimeout();
          // Only show "stopped by GM" if streaming was still active (not already timed out); the server's own time guard says so.
          if (wasStreaming) {
            setMessages(prev => [...prev, {
              id: crypto.randomUUID(),
              role: 'error' as const,
              content: reason === 'timeout' ? t('aiError.timeout') : t('ai.stoppedByGM'),
              timestamp: new Date().toISOString(),
            }]);
          }
          break;
        }
        // A call of a tool is not a line of the chat: the answer carries ONE card of what it changed (AU-18).
        case 'ai:tool-start':
        case 'ai:tool-result':
          break;
        case 'ai:turn-changes': {
          if (pendingDeltaRef.current) {
            if (rafIdRef.current) { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = 0; }
            flushTextDelta(pendingDeltaRef.current);
            pendingDeltaRef.current = '';
          }
          const turn = msg as unknown as { turnId: string; planId?: string; changes: AiChange[]; timestamp?: string };
          if (turn.planId !== undefined && applyingPlanRef.current === turn.planId) applyingPlanRef.current = null;
          setMessages(prev => itemsWithTurn(prev, turn));
          // The fix chosen in a remark's topic did something: its answer may close the remark.
          if (pendingCloseRef.current !== null && isStreamingRef.current && turn.changes.length > 0) fixAppliedRef.current = true;
          // «Do it» applied a plan / a remark's fix came back: the answer is done.
          if (hintToastRef.current && awaitingFixRef.current) {
            hintToastRef.current = false;
            toast(t('remarks.hint.marked'));
          }
          if (turn.planId !== undefined || awaitingFixRef.current) {
            awaitingFixRef.current = false;
            setStageState('done');
          }
          const waiting = pendingUndoRef.current;
          if (waiting !== null && waiting.turnId === turn.turnId) {
            pendingUndoRef.current = null;
            setUndoConflict(null);
            if (waiting.all) toast(t('aiCard.toastUndone'));
          }
          break;
        }
        case 'ai:plan': {
          const plan = msg as unknown as { planId: string; steps: AiPlanStepWire[]; timestamp?: string };
          flushPending();
          setMessages(prev => itemsWithPlan(prev, plan));
          break;
        }
        case 'ai:plan-stale': {
          const stale = msg as unknown as { planId: string; reason: 'beat_changed' | 'objective_changed' | 'missing' };
          if (applyingPlanRef.current === stale.planId) applyingPlanRef.current = null;
          setMessages(prev => itemsWithPlanStale(prev, stale));
          break;
        }
        case 'ai:plan-dismissed': {
          setMessages(prev => itemsWithPlanDismissed(prev, (msg as { planId: string }).planId));
          break;
        }
        case 'ai:needs-confirmation': {
          const ask = msg as unknown as { actionId: string; kind: 'show' | 'delete'; tool: string; target: AiOutwardData['target']; timestamp?: string; fromUntrustedSource?: true | 'unknown' };
          flushPending();
          setMessages(prev => itemsWithOutward(prev, ask));
          break;
        }
        case 'ai:confirmed': {
          const done = msg as unknown as { actionId: string };
          if (runningActionRef.current === done.actionId) runningActionRef.current = null;
          setMessages(prev => itemsWithConfirmed(prev, done));
          break;
        }
        case 'ai:action-status': {
          // P5: the server's record of the button (running / done / partial / unknown / stale…) — the click is answered.
          const frame = msg as unknown as ActionStatusFrame;
          if (runningActionRef.current === frame.actionId && frame.status !== 'running') runningActionRef.current = null;
          flushPending();
          setMessages(prev => itemsWithActionStatus(prev, frame));
          break;
        }
        case 'ai:plan-status': {
          const frame = msg as unknown as { planId: string; status: 'open' | 'applying' | 'applied' | 'unknown'; rolledBack?: 'all' | 'partial' | 'none' };
          if (applyingPlanRef.current === frame.planId && frame.status !== 'applying') applyingPlanRef.current = null;
          setMessages(prev => itemsWithPlanStatus(prev, frame));
          break;
        }
        case 'ai:options': {
          const offered = msg as unknown as { remarkKey: string; options: AiOptionsData['options'] };
          flushPending();
          setMessages(prev => [...prev.filter(m => !(m.role === 'options' && m.optionsData?.remarkKey === offered.remarkKey)), {
            id: crypto.randomUUID(), role: 'options' as const, content: '',
            optionsData: { remarkKey: offered.remarkKey, options: offered.options },
            timestamp: new Date().toISOString(),
          }]);
          break;
        }
        case 'command:error': {
          // A remark's fix / hint the server refused: the answer that was awaited will not come.
          const refused = (msg as { command?: string }).command;
          if (refused === 'remark:fix' || refused === 'remark:apply-hint') {
            if (!awaitingFixRef.current) break;
            awaitingFixRef.current = false;
            hintToastRef.current = false;
            setStageState('error');
            break;
          }
          // «Do it» / «Show to the players» the server refused (the plan or the action is gone — a restart, another tab):
          // the button says so instead of dying silently. Only the instance that sent it reacts.
          if (refused === 'ai:plan-dismiss') {
            setMessages(prev => prev.map(m => m.planData?.state === 'dismissing' ? { ...m, planData: { ...m.planData, state: 'open' as const } } : m));
            toast.error(t('ai.results.declineError'));
            break;
          }
          if (refused === 'ai:plan-apply') {
            const planId = applyingPlanRef.current;
            if (planId === null) break;
            applyingPlanRef.current = null;
            setMessages(prev => prev.map(m => (m.role === 'plan' && m.planData?.planId === planId && m.planData.state !== 'applied'
              ? { ...m, planData: { ...m.planData, state: 'stale' as const, staleReason: 'gone' as const } } : m)));
            break;
          }
          if (refused === 'ai:run-confirmed') {
            const actionId = runningActionRef.current;
            if (actionId === null) break;
            runningActionRef.current = null;
            setMessages(prev => prev.map(m => (m.role === 'outward' && m.outwardData?.actionId === actionId && !m.outwardData.done
              ? { ...m, outwardData: { ...m.outwardData, stale: true } } : m)));
            break;
          }
          // The server refused the undo (not the GM's answer / not in the journal): the card is as it was,
          // nothing waits for an answer any more, and the GM is told (the table's generic error toast skips ai:undo).
          if (refused !== 'ai:undo') break;
          // Two chats live in the table shell: only the one that sent the undo tells the GM.
          if (Date.now() - undoSentAtRef.current > 8000) break;
          undoSentAtRef.current = 0;
          pendingUndoRef.current = null;
          setUndoConflict(null);
          toast.error(t('aiCard.undoRefused'));
          break;
        }
        case 'ai:undo-conflict': {
          const found = msg as unknown as { turnId: string; conflicts: Array<{ changeId: string }> };
          setUndoConflict({
            turnId: found.turnId,
            changeIds: lastUndoIdsRef.current,
            conflicts: found.conflicts.map(c => c.changeId),
          });
          break;
        }
        case 'ai:done': {
          const finishedConversationId = (msg as { conversationId: string }).conversationId;
          const title = manualTitles.current.get(finishedConversationId)
            ?? (msg as { conversationTitle?: string }).conversationTitle;
          if (title) {
            if (conversationIdRef.current === null || conversationIdRef.current === finishedConversationId) setConversationTitle(title);
            setConversations(prev => prev.map(c => c.id === finishedConversationId ? { ...c, title } : c));
          }
          if (pendingDeltaRef.current) {
            if (rafIdRef.current) { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = 0; }
            flushTextDelta(pendingDeltaRef.current);
            pendingDeltaRef.current = '';
          }
          setIsStreaming(false);
          setStageState('done');
          clearStreamingTimeout();
          // The answer to the FIX chosen in a remark's topic is done and it changed something: the remark is closed
          // with it. An answer that changed nothing (Tia asked back) keeps the topic open (owner, 23.10).
          if (pendingCloseRef.current !== null) {
            const key = pendingCloseRef.current;
            pendingCloseRef.current = null;
            if (fixAppliedRef.current) {
              mp.send({ type: 'remark:close', key });
              endTopic(key, t('ai.topic.endedDone'));
            }
            fixAppliedRef.current = false;
          }
          // Extract emotion from ai:done and apply to last assistant message
          const rawEmotion = (msg as { emotion?: string }).emotion;
          const validEmotion = rawEmotion && PERSONA_EMOTIONS.includes(rawEmotion as PersonaEmotion)
            ? rawEmotion as PersonaEmotion
            : undefined;

          // Strip [EMOTION] line from last assistant message + set emotion
          setMessages(prev => {
            let lastIdx = -1;
            for (let i = prev.length - 1; i >= 0; i--) {
              if (prev[i].role === 'assistant') { lastIdx = i; break; }
            }
            if (lastIdx === -1) return prev;
            const updated = [...prev];
            updated[lastIdx] = {
              ...updated[lastIdx],
              content: stripEmotionLine(updated[lastIdx].content),
              emotion: validEmotion,
              suggestedReplies: (msg as { suggestedReplies?: string[] }).suggestedReplies ?? [],
            };
            return updated;
          });

          break;
        }
        case 'ai:history-summarized':
          // One quiet line: the start of a long conversation now lives in Tia's summary (30.10, act J).
          setMessages(prev => [...prev, { id: crypto.randomUUID(), role: 'notice' as const, content: t('ai.historySummarized'), timestamp: new Date().toISOString() }]);
          break;
        case 'ai:error':
          setStageState('error');
          handleAiError(
            (msg as { message: string }).message,
            (msg as { timestamp: string }).timestamp,
          );
          break;
      }
    });
    return unsub;
  }, [mp]);

  // --- sendMessage ---

  const sendMessage = useCallback(async (text: string, opts: { mode?: AiChatMode; focus?: AiFocus } = {}) => {
    if (!sessionId || loadingConversation.current) return;
    const trimmed = text.trim();
    if (!trimmed || trimmed.length > MAX_MESSAGE_LENGTH) return;
    // A player waits for his answer; the GM may send over a running one.
    if (isPlayer && isStreamingRef.current) return;

    // A new message while the AI is still working interrupts it: the server drops the running answer of this
    // conversation when the next request comes (what it already did stays on the card of that answer).
    if (isStreamingRef.current) {
      if (pendingDeltaRef.current) {
        if (rafIdRef.current) { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = 0; }
        flushTextDelta(pendingDeltaRef.current);
        pendingDeltaRef.current = '';
      }
      awaitingInterruptRef.current = true;
      setIsStreaming(false);
      isStreamingRef.current = false;
      clearStreamingTimeout();
    }

    const reqFocus = isPlayer ? undefined : (opts.focus ?? getFocusRef.current?.());
    const userMsg: AiChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: trimmed,
      timestamp: new Date().toISOString(),
      ...(reqFocus !== undefined ? { focus: reqFocus } : {}),
    };
    setMessages(prev => [...prev, userMsg]);
    setIsStreaming(true);
    setStageState('thinking');
    setError(null);
    setThinkingContent('');
    thinkingContentRef.current = '';
    setIsAborted(false);
    leaveRun();
    stopPendingRef.current = false;

    try {
      const res = await apiFetch(isPlayer ? `/api/sessions/${sessionId}/ai/player-chat` : `/api/sessions/${sessionId}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isPlayer ? {
          message: trimmed,
          conversationId: conversationIdRef.current ?? undefined,
          mode: opts.mode ?? modeRef.current,
        } : {
          message: trimmed,
          conversationId: conversationIdRef.current ?? undefined,
          mode: opts.mode ?? modeRef.current,
          model: textModelRef.current,
          imageModel: imageModelRef.current,
          focus: reqFocus,
          ...(topicRef.current !== null ? { topic: { remarkKey: topicRef.current.remarkKey } } : {}),
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        let errorMsg: string = t('aiError.sendError');
        if (data.code === 'PLAYER_AI_DISABLED') errorMsg = t('aiError.playerAiDisabled');
        else if (data.code === 'TOKEN_BUDGET_EXCEEDED') errorMsg = t('aiError.tokenBudget');
        else if (res.status === 429) errorMsg = t('aiError.rateLimit');
        else if (data.code === 'ai_unavailable') { errorMsg = isPlayer ? t('aiError.noModelPlayer') : t('aiError.noModelKey'); setAiUnavailable(true); }
        else if (res.status === 503) errorMsg = t('aiError.serviceUnavailable');
        else if (res.status === 403) errorMsg = t('aiError.gmOnly');
        else if (data.error) errorMsg = data.error;

        setMessages(prev => [...prev, {
          id: crypto.randomUUID(),
          role: 'error',
          content: errorMsg,
          timestamp: new Date().toISOString(),
        }]);
        dropTopicState();
        setIsStreaming(false);
        setStageState('error');
        awaitingInterruptRef.current = false;
        return;
      }

      awaitingInterruptRef.current = false;
      setAiUnavailable(false);
      const { conversationId: newConvId, runId } = await res.json() as { conversationId: string; runId?: string };
      if (runId !== undefined) runIdRef.current = runId;
      if (stopPendingRef.current) {
        stopPendingRef.current = false;
        leaveRun();
        mp.send({ type: 'ai:abort', conversationId: newConvId });
        return;
      }
      setConversationId(newConvId);
      resetStreamingTimeout();

    } catch {
      setMessages(prev => [...prev, {
        id: crypto.randomUUID(),
        role: 'error',
        content: t('aiError.networkError'),
        timestamp: new Date().toISOString(),
      }]);
      dropTopicState();
      setIsStreaming(false);
      setStageState('error');
    }
  }, [sessionId, isPlayer, mp]);
  const sendRef = useRef(sendMessage);
  sendRef.current = sendMessage;

  // --- resetChat ---

  const resetChat = useCallback(() => {
    if (isStreamingRef.current || loadingConversation.current) return;
    const returningToDraft = conversationIdRef.current !== null;
    conversationDrafts.current.set(conversationIdRef.current ?? 'new', currentInput.current);
    historyRequest.current++;
    conversationIdRef.current = null;
    setConversationTitle(null);
    setMessages([]);
    setConversationId(null);
    knownConvIdsRef.current = new Set();

    topicRef.current = null;
    pendingCloseRef.current = null;
    awaitingFixRef.current = false;
    hintToastRef.current = false;
    applyingPlanRef.current = null;
    runningActionRef.current = null;
    setStageState('idle');
    setIsStreaming(false);
    setError(null);
    setThinkingContent('');
    thinkingContentRef.current = '';
    setIsAborted(false);
    setInput(returningToDraft ? conversationDrafts.current.get('new') ?? '' : '');
    setUndoConflict(null);
    pendingUndoRef.current = null;
    clearStreamingTimeout();
  }, []);

  // --- abortGeneration ---

  const abortGeneration = useCallback(() => {
    if (!isStreamingRef.current) return;
    // The first message of a new chat has no conversation until the server answers: the stop waits for it (stopPendingRef).
    if (conversationIdRef.current) {
      mp.send({
        type: 'ai:abort',
        conversationId: conversationIdRef.current,
      });
    }
    // Its own «aborted» arrives later: the line below is already the stop, the frame must not stop a next answer.
    if (runIdRef.current === null) stopPendingRef.current = true;
    leaveRun();
    // Optimistic: immediately stop streaming UI so the button feels responsive.
    // Server will send ai:aborted later, but we update UI now.
    if (pendingDeltaRef.current) {
      if (rafIdRef.current) { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = 0; }
      flushTextDelta(pendingDeltaRef.current);
      pendingDeltaRef.current = '';
    }
    dropTopicState();
    setIsStreaming(false);
    setIsAborted(true);
    setStageState('idle');
    clearStreamingTimeout();
    setMessages(prev => [...prev, {
      id: crypto.randomUUID(),
      role: 'error' as const,
      content: t('ai.stoppedByGM'),
      timestamp: new Date().toISOString(),
    }]);
  }, [mp, t]);

  // Results are restored only when their conversation is explicitly opened.

  // --- «Plan», the button outward, a remark's topic ---

  const applyPlan = useCallback((planId: string, stepIds: string[]) => {
    applyingPlanRef.current = planId;
    setMessages(prev => prev.map(m => m.planData?.planId === planId ? { ...m, planData: { ...m.planData, state: 'applying' as const } } : m));
    mp.send({ type: 'ai:plan-apply', planId, stepIds });
  }, [mp]);

  const dismissPlan = useCallback((planId: string) => {
    setMessages(prev => prev.map(m => m.planData?.planId === planId ? { ...m, planData: { ...m.planData, state: 'dismissing' as const } } : m));
    mp.send({ type: 'ai:plan-dismiss', planId });
  }, [mp]);

  const runConfirmed = useCallback((actionId: string) => {
    runningActionRef.current = actionId;
    mp.send({ type: 'ai:run-confirmed', actionId });
  }, [mp]);

  const messagesRef = useRef<AiChatMessage[]>([]);
  messagesRef.current = messages;

  const pickOption = useCallback((optionsMessageId: string, optionId: string) => {
    const holder = messagesRef.current.find(m => m.id === optionsMessageId);
    const option = holder?.optionsData?.options.find(o => o.id === optionId);
    if (holder?.optionsData === undefined || option === undefined) return;
    const key = holder.optionsData.remarkKey;
    if (option.kind === 'dismiss') {
      mp.send({ type: 'remark:dismiss', key });
      endTopic(key, t('ai.topic.ended').replace('{option}', option.label.toLowerCase()));
      return;
    }
    // «fix» / «ask»: the option's request goes as asked, whatever the box says. Only a fix closes the remark — and only
    // once its answer has changed something (owner, 23.10: «Объясни понятнее» closed the remark as «done»); a question
    // goes on in the same topic.
    setMessages(prev => prev.filter(m => m.id !== optionsMessageId));
    pendingCloseRef.current = option.kind === 'fix' ? key : null;
    fixAppliedRef.current = false;
    void sendRef.current(option.prompt ?? option.label, { mode: 'auto' });
  }, [mp, t]);

  const closeTopic = useCallback((topicMessageId: string) => {
    const holder = messagesRef.current.find(m => m.id === topicMessageId);
    if (holder?.topicData === undefined || !holder.topicData.open) return;
    endTopic(holder.topicData.remarkKey, t('ai.topic.closed'));
  }, [t]);

  // «Замечания» → this chat: a request to Tia, a fix the server does as one answer, a hint to mark.
  const bridgeOpen = options.bridge?.open;
  const hasBridge = options.bridge !== undefined;
  useEffect(() => {
    if (!hasBridge) return;
    const open = (): void => { bridgeOpenRef.current?.(); };
    /** A name for the conversation of a fix: this chat's, or its own until Tia is asked something. */
    const conversationForAction = (): string => {
      if (conversationIdRef.current) return conversationIdRef.current;
      const id = crypto.randomUUID();
      conversationIdRef.current = id;
      knownConvIdsRef.current.add(id);
      setConversationId(id);
      return id;
    };
    const line = (text: string): void => {
      setMessages(prev => [...prev, { id: crypto.randomUUID(), role: 'user' as const, content: text, timestamp: new Date().toISOString() }]);
    };
    const offs = [
      onAiAsk((req) => {
        open();
        if (req.topic !== undefined) {
          const topic = req.topic;
          // A new topic closes the one before it silently.
          setMessages(prev => [
            ...prev.map(m => (m.role === 'topic' && m.topicData?.open ? { ...m, topicData: { ...m.topicData, open: false } } : m)),
            { id: crypto.randomUUID(), role: 'topic' as const, content: '', topicData: { remarkKey: topic.remarkKey, title: topic.title, open: true }, timestamp: new Date().toISOString() },
          ]);
          topicRef.current = { remarkKey: topic.remarkKey };
        }
        void sendRef.current(req.message, { mode: 'auto', ...(req.focus !== undefined ? { focus: req.focus } : {}) });
        return true;
      }),
      onRemarkFix((req) => {
        open();
        const conversationId = conversationForAction();
        line(t('ai.remark.fixLine').replace('{title}', req.title));
        hintToastRef.current = false;
        awaitingFixRef.current = true;
        setStageState('thinking');
        mp.send({ type: 'remark:fix', key: req.key, conversationId });
        return true;
      }),
      onApplyHint((req) => {
        open();
        const conversationId = conversationForAction();
        line(t('ai.remark.hintLine').replace('{title}', req.title));
        hintToastRef.current = true;
        awaitingFixRef.current = true;
        setStageState('thinking');
        mp.send({ type: 'remark:apply-hint', key: req.key, conversationId });
        return true;
      }),
      onRemarksFound((count) => {
        setNotesCount(count);
        if (count > 0) setStageState(prev => (prev === 'thinking' ? prev : 'notes'));
        return true;
      }),
    ];
    return () => { for (const off of offs) off(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBridge, mp]);
  const bridgeOpenRef = useRef(bridgeOpen);
  bridgeOpenRef.current = bridgeOpen;

  // --- undoTurn: by the server's change journal (AU-6) ---

  const undoTurn = useCallback((turnId: string, changeIds?: string[]) => {
    if (!sessionId) return;
    pendingUndoRef.current = { turnId, all: changeIds === undefined };
    lastUndoIdsRef.current = changeIds;
    undoSentAtRef.current = Date.now();
    mp.send({ type: 'ai:undo', turnId, ...(changeIds !== undefined ? { changeIds } : {}) });
  }, [mp, sessionId]);

  const resolveUndoConflict = useCallback((choice: 'restore' | 'keep-mine' | 'cancel') => {
    const conflict = undoConflict;
    setUndoConflict(null);
    if (conflict === null) return;
    // The question was answered: the undo that follows is not «without a conflict» (no toast), whatever the choice.
    pendingUndoRef.current = null;
    if (choice === 'cancel') return;
    undoSentAtRef.current = Date.now();
    mp.send({ type: 'ai:undo', turnId: conflict.turnId, ...(conflict.changeIds !== undefined ? { changeIds: conflict.changeIds } : {}), resolve: choice });
  }, [mp, undoConflict]);

  // --- loadConversations ---

  const loadConversations = useCallback(async (append = false) => {
    if (!sessionId) return;
    setIsLoadingHistory(true);
    try {
      const res = await apiFetch(`/api/sessions/${sessionId}/ai/conversations?limit=20&offset=${append ? conversations.length : 0}`);
      if (!res.ok) throw new Error(t('ai.historyLoadError'));
      const data = await res.json();
      setConversations(prev => append ? [...prev, ...data.conversations.filter((c: AiConversationSummary) => !prev.some(p => p.id === c.id))] : data.conversations);
      setConversationsTotal(data.total);
    }
    finally { setIsLoadingHistory(false); }
  }, [sessionId, conversations.length, t]);

  // --- loadConversation ---

  const loadConversation = useCallback(async (convId: string) => {
    if (!sessionId || convId === conversationIdRef.current) return;
    if (isStreamingRef.current || loadingConversation.current) throw new Error(t('ai.historyBusy'));
    const request = ++historyRequest.current;
    loadingConversation.current = true;
    setIsLoadingHistory(true);
    try {
      const res = await apiFetch(`/api/sessions/${sessionId}/ai/conversations/${convId}`);
      if (!res.ok) throw new Error(t('ai.historyLoadError'));
      {
        const data = await res.json();
        if (request !== historyRequest.current) return;
        conversationDrafts.current.set(conversationIdRef.current ?? 'new', currentInput.current);
        conversationIdRef.current = convId;
        knownConvIdsRef.current = new Set([convId]);

        setConversationId(convId);
        setConversationTitle(data.title ?? null);
        // Map server messages to client AiChatMessage format
        const clientMessages: AiChatMessage[] = data.messages
          .map((m: { role: string; content: unknown; timestamp: string; suggestedReplies?: string[] }) => ({
            id: crypto.randomUUID(),
            role: m.role === 'assistant' ? 'assistant' as const : 'user' as const,
            content: typeof m.content === 'string' ? m.content : m.role === 'assistant' && Array.isArray(m.content)
              ? m.content.filter((b: { type?: string; text?: unknown }) => b.type === 'text' && typeof b.text === 'string').map((b: { text: string }) => b.text).join('\n') : '',
            timestamp: m.timestamp,
            suggestedReplies: m.role === 'assistant' ? m.suggestedReplies : undefined,
          })).filter((m: AiChatMessage) => m.content !== '');
        setMessages(clientMessages);
        setInput(conversationDrafts.current.get(convId) ?? '');
        setThinkingContent('');
        setError(null);
        setIsAborted(false);
        setStageState('idle');
        setUndoConflict(null);
        pendingUndoRef.current = null;
        topicRef.current = null;
        pendingCloseRef.current = null;
        awaitingFixRef.current = false;
        hintToastRef.current = false;
        applyingPlanRef.current = null;
        runningActionRef.current = null;
        thinkingContentRef.current = '';
        pendingDeltaRef.current = '';
        if (rafIdRef.current) { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = 0; }
        mp.send({ type: 'ai:turns-get', conversationId: convId });
      }
    }
    finally { loadingConversation.current = false; setIsLoadingHistory(false); }
  }, [sessionId, mp, t]);


  const renameConversation = useCallback(async (id: string, title: string) => {
    if (!sessionId) return;
    const res = await apiFetch(`/api/sessions/${sessionId}/ai/conversations/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title }),
    });
    if (!res.ok) throw new Error(t('ai.historyRenameError'));
    const data = await res.json();
    manualTitles.current.set(id, data.title);
    setConversations(prev => prev.map(c => c.id === id ? { ...c, title: data.title } : c));
    if (conversationIdRef.current === id) setConversationTitle(data.title);
  }, [sessionId, t]);

  // Derive lastEmotion from messages
  let lastEmotion: PersonaEmotion | undefined;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'assistant' && messages[i].emotion) {
      lastEmotion = messages[i].emotion;
      break;
    }
  }

  return {
    messages, isStreaming, error, conversationId, sendMessage, resetChat,
    allowEdits, setAllowEdits, stageState, notesCount, aiUnavailable, getFocus: () => getFocusRef.current?.(),
    applyPlan, dismissPlan, hiddenResultIds, setResultHidden, runConfirmed, pickOption, closeTopic,
    textModel, setTextModel, imageModel, setImageModel, undoTurn, undoConflict, resolveUndoConflict, thinkingContent, abortGeneration,
    isAborted, input, setInput, conversations, loadConversations, loadConversation,
    conversationTitle, conversationsTotal, renameConversation,
    isLoadingHistory, lastEmotion,
  };
}

// --- @Tia in the table chat: the cards under her reply ---

/** The id of Tia's reply in the chat a frame of an @Tia answer belongs to (chatMessageId, or the `mention:<id>` conversation). */
function mentionIdOf(msg: { chatMessageId?: string; conversationId?: string }): string | undefined {
  if (msg.chatMessageId !== undefined) return msg.chatMessageId;
  if (typeof msg.conversationId === 'string' && msg.conversationId.startsWith('mention:')) return msg.conversationId.slice('mention:'.length);
  return undefined;
}

export interface UseMentionCardsReturn {
  /** chatMessageId → the card / plan / button outward of that answer (only the one who asked ever gets them). */
  itemsFor: (chatMessageId: string) => AiChatMessage[];
  undoTurn: (turnId: string, changeIds?: string[]) => void;
  applyPlan: (planId: string, stepIds: string[]) => void;
  dismissPlan: (planId: string) => void;
  runConfirmed: (actionId: string) => void;
}

export function useMentionCards(): UseMentionCardsReturn {
  const { mp } = useSessionContext();
  const [items, setItems] = useState<Record<string, AiChatMessage[]>>({});
  useEffect(() => {
    mp.send?.({ type: 'ai:turns-get' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mp.session?.id]);
  useEffect(() => {
    if (mp.onMessage === undefined) return;
    const update = (id: string, f: (prev: AiChatMessage[]) => AiChatMessage[]): void => {
      setItems(prev => ({ ...prev, [id]: f(prev[id] ?? []) }));
    };
    return mp.onMessage((msg) => {
      if (!msg.type.startsWith('ai:')) return;
      const id = mentionIdOf(msg as { chatMessageId?: string; conversationId?: string });
      if (id === undefined) return;
      switch (msg.type) {
        case 'ai:turn-changes': {
          const turn = msg as unknown as { turnId: string; planId?: string; changes: AiChange[]; timestamp?: string };
          update(id, prev => itemsWithTurn(prev, turn));
          break;
        }
        case 'ai:plan': {
          const plan = msg as unknown as { planId: string; steps: AiPlanStepWire[]; timestamp?: string };
          update(id, prev => itemsWithPlan(prev, plan));
          break;
        }
        case 'ai:plan-stale': {
          const stale = msg as unknown as { planId: string; reason: 'beat_changed' | 'objective_changed' | 'missing' };
          update(id, prev => itemsWithPlanStale(prev, stale));
          break;
        }
        case 'ai:plan-dismissed': {
          update(id, prev => itemsWithPlanDismissed(prev, (msg as { planId: string }).planId));
          break;
        }
        case 'ai:needs-confirmation': {
          const ask = msg as unknown as { actionId: string; kind: 'show' | 'delete'; tool: string; target: AiOutwardData['target']; timestamp?: string; fromUntrustedSource?: true | 'unknown' };
          update(id, prev => itemsWithOutward(prev, ask));
          break;
        }
        case 'ai:confirmed': {
          const done = msg as unknown as { actionId: string };
          update(id, prev => itemsWithConfirmed(prev, done));
          break;
        }
        case 'ai:action-status': {
          const frame = msg as unknown as ActionStatusFrame;
          update(id, prev => itemsWithActionStatus(prev, frame));
          break;
        }
        case 'ai:plan-status': {
          const frame = msg as unknown as { planId: string; status: 'open' | 'applying' | 'applied' | 'unknown'; rolledBack?: 'all' | 'partial' | 'none' };
          update(id, prev => itemsWithPlanStatus(prev, frame));
          break;
        }
      }
    });
  }, [mp]);
  const itemsFor = useCallback((chatMessageId: string): AiChatMessage[] => items[chatMessageId] ?? [], [items]);
  const undoTurn = useCallback((turnId: string, changeIds?: string[]) => {
    mp.send({ type: 'ai:undo', turnId, ...(changeIds !== undefined ? { changeIds } : {}) });
  }, [mp]);
  const applyPlan = useCallback((planId: string, stepIds: string[]) => { mp.send({ type: 'ai:plan-apply', planId, stepIds }); }, [mp]);
  const dismissPlan = useCallback((planId: string) => { mp.send({ type: 'ai:plan-dismiss', planId }); }, [mp]);
  const runConfirmed = useCallback((actionId: string) => { mp.send({ type: 'ai:run-confirmed', actionId }); }, [mp]);
  return { itemsFor, undoTurn, applyPlan, dismissPlan, runConfirmed };
}

// ── One GM chat for the table and the Studio ──

/**
 * StudioGate swaps the table and the Studio (one unmounts the other), so a chat per surface died with it: leaving the
 * Studio closed the conversation and an answer still running on the server lost its window (owner 31.10: «чат
 * сворачивается при сворачивании GMSTUDIO»). The chat lives above the swap (SharedAiChatProvider, App.tsx); each surface
 * hands it only what is its own — where the GM looks, how a target opens, how the chat is shown.
 */
interface SharedAiChat {
  chat: UseAiChatReturn;
  register: (surface: { current: UseAiChatOptions }) => () => void;
}
const SharedAiChatContext = createContext<SharedAiChat | null>(null);

export function SharedAiChatProvider({ children }: { children: ReactNode }): ReactElement {
  const surfaceRef = useRef<{ current: UseAiChatOptions } | null>(null);
  const [, setRevision] = useState(0);
  const opts = surfaceRef.current?.current;
  const chat = useAiChatCore({
    getFocus: () => surfaceRef.current?.current.getFocus?.(),
    // Present only while the mounted surface can open a target: without it the server is told nothing (no receipt).
    ...(opts?.onOpenEntity !== undefined ? { onOpenEntity: (target, how) => surfaceRef.current!.current.onOpenEntity!(target, how) } : {}),
    ...(opts?.bridge !== undefined ? { bridge: { open: () => surfaceRef.current?.current.bridge?.open() } } : {}),
  });
  const register = useCallback((surface: { current: UseAiChatOptions }) => {
    surfaceRef.current = surface;
    setRevision(n => n + 1);
    return () => {
      if (surfaceRef.current !== surface) return;
      surfaceRef.current = null;
      setRevision(n => n + 1);
    };
  }, []);
  const value = useMemo(() => ({ chat, register }), [chat, register]);
  return createElement(SharedAiChatContext.Provider, { value }, children);
}

function useSurfaceAiChat(shared: SharedAiChat, options: UseAiChatOptions): UseAiChatReturn {
  const latest = useRef(options);
  latest.current = options;
  const { register } = shared;
  useEffect(() => register(latest), [register]);
  return shared.chat;
}

/** The GM's (and the table's) chat: the shared one under SharedAiChatProvider, its own elsewhere (a player chat, tests). */
export function useAiChat(options: UseAiChatOptions = {}): UseAiChatReturn {
  const shared = useContext(SharedAiChatContext);
  // The provider is mounted once above the session tree and never comes and goes under a mounted chat: the branch is
  // fixed for a component's whole life, so the hook order holds.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return shared !== null && options.player !== true ? useSurfaceAiChat(shared, options) : useAiChatCore(options);
}
